/**
 * Brand-key vocabulary and layered resolver for ADR 115 (multi-brand
 * resolution and SMS whitelabel extension).
 *
 * The {@link BrandKey} enum and {@link resolveBrandKey} pure function are
 * the authoritative brand-resolution surface. There is no
 * `getBrandMode()` / `ThemeMode` facade — all consumers call
 * {@link resolveBrandKey} directly and map the resulting
 * {@link BrandKey} to a theme via `makeTheme(brandKey)`.
 *
 * Resolution model (per ADR 115 §3):
 *
 * 1. `jwtClaim` — post-auth authoritative signal, derived at JWT mint
 *    time from `user.tenant.brand_key` when the user's tenancy is
 *    uniformly classified (uniform-tenant rule).
 * 2. `hostname` — fallback, mapped via {@link BRAND_HOSTNAMES}. Pre-auth
 *    surfaces, tenantless users, and unclassified/mixed-tenancy users
 *    (whose tokens carry no usable claim) all resolve here.
 * 3. `fallback` — app default; always `BrandKey.Aegix` unless overridden.
 *
 * The resolver is a pure function. No `window` access inside — callers
 * feed it. This keeps the resolver SSR-safe, testable in isolation, and
 * unambiguous about what inputs drive the decision.
 *
 * Naming hygiene (per ADR 115 §2): `safe-plus` is the wire-format and
 * asset-directory identifier. The user-visible display string is
 * `SAFE+`. The deep-link URL scheme is `safeplus://` (independent —
 * registered with iOS/Android). Never collapse these three.
 */

/**
 * Closed vendor-identity enum. Wire-format values match the spelling
 * used in JWT claims, DB columns, asset directory names, and API JSON.
 *
 * Additions require a new ADR (per ADR 115 §1).
 */
export const BrandKey = {
  Aegix: 'aegix',
  SafePlus: 'safe-plus',
  Dirs: 'dirs',
} as const;

export type BrandKey = (typeof BrandKey)[keyof typeof BrandKey];

export const ALL_BRAND_KEYS: ReadonlyArray<BrandKey> = Object.freeze([
  BrandKey.Aegix,
  BrandKey.SafePlus,
  BrandKey.Dirs,
]);

/**
 * User-visible display strings, indexed by brand-key.
 *
 * Per ADR 115 §2: the wire-format identifier (`safe-plus`) and the
 * display string (`SAFE+`) are intentionally distinct.
 */
export const BRAND_DISPLAY_NAMES: Readonly<Record<BrandKey, string>> =
  Object.freeze({
    [BrandKey.Aegix]: 'Aegix',
    [BrandKey.SafePlus]: 'SAFE+',
    [BrandKey.Dirs]: 'DIRS',
  });

/**
 * Brand → app → hostname-pattern map.
 *
 * The `{env}` placeholder is substituted at the point of use (Phase 4
 * redirect-URL construction will read the current environment). This
 * map is the source of truth for both the redirect response field and
 * outbound-email "click here to sign in" URLs.
 *
 * A brand missing an app entry means that app is not (yet) whitelabel-
 * ready for that brand — for example, `safe-plus` has no `one` entry
 * today; the resolver will fall back to the app's default brand-key
 * for SAFE+ parents using Aegix One.
 */
export const BRAND_HOSTNAMES: Readonly<
  Record<BrandKey, Readonly<Record<string, string>>>
> = Object.freeze({
  [BrandKey.Aegix]: Object.freeze({
    aim: 'aim.{env}.aegix.global',
    sms: 'sms.{env}.aegix.global',
    one: 'one.{env}.aegix.global',
    beacon: 'beacon.{env}.aegix.global',
  }),
  [BrandKey.SafePlus]: Object.freeze({
    aim: 'safe.{env}.aegix.global',
    sms: 'safe-ms.{env}.aegix.global',
  }),
  [BrandKey.Dirs]: Object.freeze({
    aim: '{env}.dirs.app',
  }),
});

/**
 * Hostname-subdomain → brand-key map for pre-auth resolution.
 *
 * Subdomain matching is **exact**, not substring — this is a deliberate
 * regression guard against the legacy `hostname.includes('safe')`
 * behavior that this ADR replaces. A hostname like `unsafe.example.com`
 * must NOT resolve to `safe-plus`.
 */
const SUBDOMAIN_TO_BRAND: Readonly<Record<string, BrandKey>> = Object.freeze({
  aim: BrandKey.Aegix,
  sms: BrandKey.Aegix,
  one: BrandKey.Aegix,
  beacon: BrandKey.Aegix,
  safe: BrandKey.SafePlus,
  'safe-ms': BrandKey.SafePlus,
});

const DIRS_SUFFIX = '.dirs.app';

function leftmostSubdomain(host: string): string {
  if (!host) return '';
  const stripped = host.toLowerCase().split(':', 1)[0];
  const dotIndex = stripped.indexOf('.');
  return dotIndex === -1 ? '' : stripped.slice(0, dotIndex);
}

/**
 * Map a request hostname to a brand-key, or return `null` if unknown.
 *
 * DIRS `*.dirs.app` always wins regardless of subdomain. All other
 * matches require an exact subdomain hit against the internal subdomain
 * map.
 *
 * Returns `null` for unknown hosts so the caller can apply its own
 * fallback policy (typically {@link BrandKey.Aegix}).
 */
export function hostnameToBrandKey(
  hostname: string | null | undefined,
): BrandKey | null {
  if (!hostname) return null;
  const host = hostname.toLowerCase().split(':', 1)[0];
  if (host === 'dirs.app' || host.endsWith(DIRS_SUFFIX)) {
    return BrandKey.Dirs;
  }
  const sub = leftmostSubdomain(host);
  return SUBDOMAIN_TO_BRAND[sub] ?? null;
}

function isBrandKey(value: unknown): value is BrandKey {
  return (
    typeof value === 'string' &&
    (ALL_BRAND_KEYS as ReadonlyArray<string>).includes(value)
  );
}

function coerceBrandKey(value: unknown): BrandKey | null {
  return isBrandKey(value) ? value : null;
}

export interface ResolveBrandKeyInput {
  /**
   * Brand-key from JWT (post-auth signal). May be a {@link BrandKey},
   * the wire-format string, or `null`/`undefined`. Unknown strings are
   * ignored (treated as absent) per the ADR 107 contract.
   */
  jwtClaim?: BrandKey | string | null;

  /**
   * Request hostname (pre-auth signal). May be `null`/`undefined`.
   * Typically `window.location.hostname` on the client.
   */
  hostname?: string | null;

  /**
   * Set of brand-keys the calling application can render. If omitted,
   * any known brand-key is accepted. Pass an explicit set on the
   * frontend (e.g., `new Set([BrandKey.Aegix])` for sms-web today;
   * `new Set([BrandKey.Aegix, BrandKey.SafePlus])` after Phase 2).
   */
  supportedBrands?: ReadonlySet<BrandKey> | Iterable<BrandKey | string>;

  /**
   * Brand-key to return when no signal resolves to a supported brand.
   * Defaults to {@link BrandKey.Aegix}.
   */
  fallback?: BrandKey;
}

/**
 * Resolve a brand-key from layered inputs.
 *
 * Resolution order (per ADR 115 §3):
 *
 * 1. `jwtClaim` if it parses to a known {@link BrandKey} and is in
 *    `supportedBrands` (or `supportedBrands` is omitted = "accept
 *    anything known").
 * 2. Hostname-mapped brand if it resolves and is supported.
 * 3. `fallback`.
 *
 * Pure function. Never returns `null` — callers can rely on this.
 */
export function resolveBrandKey(input: ResolveBrandKeyInput = {}): BrandKey {
  const {
    jwtClaim,
    hostname,
    supportedBrands,
    fallback = BrandKey.Aegix,
  } = input;

  const supportedSet: ReadonlySet<BrandKey> | null = supportedBrands
    ? new Set(
        Array.from(supportedBrands, (b) => coerceBrandKey(b)).filter(
          (b): b is BrandKey => b !== null,
        ),
      )
    : null;

  const isSupported = (brand: BrandKey): boolean =>
    supportedSet === null || supportedSet.has(brand);

  const claimBrand = coerceBrandKey(jwtClaim);
  if (claimBrand !== null && isSupported(claimBrand)) {
    return claimBrand;
  }

  const hostBrand = hostnameToBrandKey(hostname);
  if (hostBrand !== null && isSupported(hostBrand)) {
    return hostBrand;
  }

  return fallback;
}

/**
 * Return the brand-correct hostname for `(brandKey, app, env)`, or
 * `null` if the brand does not (yet) support the app.
 *
 * Convenience for redirect-URL construction (Phase 4) and outbound-
 * email "click here to sign in" link generation. Substitutes `{env}`
 * in the pattern from {@link BRAND_HOSTNAMES}.
 */
export function hostnameFor(
  brandKey: BrandKey | string,
  app: string,
  env = 'prod',
): string | null {
  const coerced = coerceBrandKey(brandKey);
  if (coerced === null) return null;
  const pattern = BRAND_HOSTNAMES[coerced][app];
  if (!pattern) return null;
  return pattern.replace('{env}', env);
}
